import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ClosingCTA } from "@/pages/Home";
import { SpendCalculator, FeeTransparencyMock } from "@/components/mocks/ProductMocks";

const FaqItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border border-[#D8E2E6] bg-white" data-testid={`faq-item-${i}`}>
      <button
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`faq-toggle-${i}`}
      >
        <span className="font-display font-bold tracking-tight">{q}</span>
        <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#0E7A8F]" : ""}`} aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-sm text-[#4A5A63] leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Pricing() {
  const { pricing, faqs } = useContent();
  return (
    <>
      <section className="border-b border-[#D8E2E6]" data-testid="pricing-hero">
        <div className="container-riq pt-16 pb-20 lg:pt-24 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">Pricing</p>
            <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.1}>Media at cost.</MaskedLine>
              <MaskedLine delay={0.22}>
                One disclosed <span className="accent-word">fee.</span>
              </MaskedLine>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#4A5A63] leading-relaxed max-w-xl">{pricing.model_note}</p>
          </div>
          <div className="lg:col-span-5">
            <FeeTransparencyMock />
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="pricing-tiers">
        <div className="container-riq">
          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {pricing.tiers.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.1} className="h-full">
                <article
                  className={`relative flex flex-col h-full p-7 rounded-xl ${t.featured ? "bg-[#101820] text-[#F7F9FA]" : "bg-white border border-[#D8E2E6]"}`}
                  style={{ boxShadow: t.featured ? "0 24px 48px -16px rgba(16,24,32,0.45)" : "0 1px 2px rgba(16,24,32,0.05), 0 16px 40px -16px rgba(16,24,32,0.12)" }}
                  data-testid={`pricing-tier-${t.id}`}
                >
                  {t.featured && (
                    <span className="absolute -top-3.5 left-6 font-mono2 text-[10px] font-bold uppercase tracking-widest bg-[#32A2C5] text-[#101820] px-3 py-1 rounded-md">
                      Most popular
                    </span>
                  )}
                  <h2 className="font-display font-black text-2xl tracking-tight">{t.name}</h2>
                  <p className={`text-xs mt-1.5 ${t.featured ? "text-[#F7F9FA]/60" : "text-[#4A5A63]"}`}>{t.target}</p>
                  <div className="mt-6 pb-6 border-b-2 border-current/20">
                    <p className="font-display font-black text-5xl tracking-tight" data-testid={`tier-fee-${t.id}`}>
                      {t.fee}
                    </p>
                    <p className={`font-mono2 text-xs uppercase tracking-widest mt-1 ${t.featured ? "text-[#0E7A8F]" : "text-[#4A5A63]"}`}>{t.fee_label}</p>
                    <p className={`font-mono2 text-[10px] mt-2 ${t.featured ? "text-[#F7F9FA]/50" : "text-[#4A5A63]"}`}>{t.min}</p>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check className="w-4 h-4 text-[#0E7A8F] mt-0.5 shrink-0" aria-hidden="true" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/book-demo"
                    className={`mt-8 ${t.featured ? "btn-primary" : "btn-outline"} w-full`}
                    data-testid={`tier-cta-${t.id}`}
                  >
                    {t.cta}
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-[#4A5A63]">
            Every tier includes media at pure cost, log-level data access and all seven channels.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#EDF3F5] border-y border-[#D8E2E6]" data-testid="pricing-calculator">
        <div className="container-riq">
          <SpendCalculator />
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="pricing-faq">
        <div className="container-riq max-w-3xl">
          <Reveal>
            <p className="eyebrow mb-3">FAQ</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-10">
              Asked, <span className="accent-word">answered.</span>
            </h2>
          </Reveal>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} i={i} />
            ))}
          </div>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
